if (!customElements.get('wacc-header')) {
  class WaccHeader extends HTMLElement {
    connectedCallback() {
      this.mobileMenu = this.querySelector('#wacc-mobile-menu');
      this.openMobileButton = this.querySelector('[data-wacc-menu-open]');
      this.closeMobileButton = this.querySelector('[data-wacc-menu-close]');
      this.mobileShopToggle = this.querySelector('[data-wacc-mobile-shop-toggle]');
      this.mobileShopPanel = this.querySelector('[data-wacc-mobile-shop-panel]');
      this.shopRoot = this.querySelector('[data-wacc-shop]');
      this.shopTrigger = this.querySelector('[data-wacc-shop-trigger]');
      this.shopMenu = this.querySelector('[data-wacc-shop-menu]');
      this.megaLinks = Array.from(this.querySelectorAll('[data-wacc-mega-link]'));
      this.previewPanels = Array.from(this.querySelectorAll('[data-wacc-preview-panel]'));
      this.searchToggle = this.querySelector('[data-wacc-search-toggle]');
      this.searchPanel = this.querySelector('[data-wacc-search-panel]');
      this.searchInput = this.querySelector('[data-wacc-search-input]');
      this.searchClose = this.querySelector('[data-wacc-search-close]');
      this.closeTimer = null;

      this.handleScroll = this.handleScroll.bind(this);
      this.handleDocumentKeydown = this.handleDocumentKeydown.bind(this);
      this.handleShopFocusOut = this.handleShopFocusOut.bind(this);
      this.handleShopKeydown = this.handleShopKeydown.bind(this);
      this.handleMouseEnter = this.openShopMenu.bind(this);
      this.handleMouseLeave = this.scheduleShopClose.bind(this);
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.handleSearchOutsideClick = this.handleSearchOutsideClick.bind(this);

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      document.addEventListener('keydown', this.handleDocumentKeydown);
      document.addEventListener('click', this.handleSearchOutsideClick);
      this.handleScroll();

      this.openMobileButton?.addEventListener('click', () => this.openMobileMenu());
      this.closeMobileButton?.addEventListener('click', () => this.closeMobileMenu());
      this.mobileShopToggle?.addEventListener('click', () => this.toggleMobileShop());
      this.searchToggle?.addEventListener('click', () => this.toggleSearch());
      this.searchClose?.addEventListener('click', () => this.closeSearch());

      this.shopRoot?.addEventListener('mouseenter', this.handleMouseEnter);
      this.shopRoot?.addEventListener('mouseleave', this.handleMouseLeave);
      this.shopRoot?.addEventListener('focusin', () => this.openShopMenu());
      this.shopRoot?.addEventListener('focusout', this.handleShopFocusOut);
      this.shopRoot?.addEventListener('keydown', this.handleShopKeydown);
      this.shopTrigger?.addEventListener('click', () => this.toggleShopMenu());

      this.megaLinks.forEach((link, index) => {
        link.addEventListener('mouseenter', () => this.activatePreview(index));
        link.addEventListener('focus', () => this.activatePreview(index));
      });

      if (window.subscribe && typeof PUB_SUB_EVENTS !== 'undefined') { /* WA-010b */
        this.unsubscribeCart = window.subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.handleScroll);
      document.removeEventListener('keydown', this.handleDocumentKeydown);
      document.removeEventListener('click', this.handleSearchOutsideClick);
      this.shopRoot?.removeEventListener('mouseenter', this.handleMouseEnter);
      this.shopRoot?.removeEventListener('mouseleave', this.handleMouseLeave);
      this.shopRoot?.removeEventListener('focusout', this.handleShopFocusOut);
      this.shopRoot?.removeEventListener('keydown', this.handleShopKeydown);
      if (typeof this.unsubscribeCart === 'function') this.unsubscribeCart();
    }

    handleScroll() {
      this.classList.toggle('is-stuck', window.scrollY > 8);
    }

    openMobileMenu() {
      if (!this.mobileMenu) return;
      this.mobileMenu.classList.add('is-open');
      this.mobileMenu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this.closeMobileButton?.focus();
    }

    closeMobileMenu() {
      if (!this.mobileMenu) return;
      this.mobileMenu.classList.remove('is-open');
      this.mobileMenu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      this.openMobileButton?.focus();
    }

    toggleMobileShop() {
      if (!this.mobileShopToggle || !this.mobileShopPanel) return;
      const isOpen = this.mobileShopToggle.getAttribute('aria-expanded') === 'true';
      this.mobileShopToggle.setAttribute('aria-expanded', String(!isOpen));
      this.mobileShopPanel.classList.toggle('is-open', !isOpen);
    }

    openShopMenu() {
      window.clearTimeout(this.closeTimer);
      this.shopMenu?.classList.add('is-open');
      this.shopTrigger?.setAttribute('aria-expanded', 'true');
    }

    closeShopMenu() {
      this.shopMenu?.classList.remove('is-open');
      this.shopTrigger?.setAttribute('aria-expanded', 'false');
      this.activatePreview(0);
    }

    scheduleShopClose() {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => this.closeShopMenu(), 120);
    }

    toggleShopMenu() {
      const isOpen = this.shopTrigger?.getAttribute('aria-expanded') === 'true';
      if (isOpen) {
        this.closeShopMenu();
      } else {
        this.openShopMenu();
        this.megaLinks[0]?.focus();
      }
    }

    activatePreview(index) {
      this.megaLinks.forEach((link, linkIndex) => {
        const isActive = linkIndex === index;
        link.classList.toggle('is-active', isActive);
        link.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.previewPanels.forEach((panel, panelIndex) => {
        panel.classList.toggle('is-active', panelIndex === index);
      });
    }

    handleShopFocusOut(event) {
      if (event.relatedTarget && this.shopRoot?.contains(event.relatedTarget)) return;
      this.closeShopMenu();
    }

    handleShopKeydown(event) {
      const activeIndex = this.megaLinks.findIndex((link) => link === document.activeElement);

      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        event.preventDefault();
        const nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % this.megaLinks.length;
        this.activatePreview(nextIndex);
        this.megaLinks[nextIndex]?.focus();
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        event.preventDefault();
        const previousIndex = activeIndex <= 0 ? this.megaLinks.length - 1 : activeIndex - 1;
        this.activatePreview(previousIndex);
        this.megaLinks[previousIndex]?.focus();
      }

      if (event.key === 'Home') {
        event.preventDefault();
        this.activatePreview(0);
        this.megaLinks[0]?.focus();
      }

      if (event.key === 'End') {
        event.preventDefault();
        const lastIndex = this.megaLinks.length - 1;
        this.activatePreview(lastIndex);
        this.megaLinks[lastIndex]?.focus();
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeShopMenu();
        this.shopTrigger?.focus();
      }
    }

    handleDocumentKeydown(event) {
      if (event.key !== 'Escape') return;
      if (this.searchPanel && !this.searchPanel.hidden) {
        this.closeSearch();
        return;
      }
      if (this.mobileMenu?.classList.contains('is-open')) {
        this.closeMobileMenu();
        return;
      }

      if (this.shopMenu?.classList.contains('is-open')) {
        this.closeShopMenu();
        this.shopTrigger?.focus();
      }
    }

    openSearch() {
      if (!this.searchPanel) return;
      this.searchPanel.hidden = false;
      this.searchToggle?.setAttribute('aria-expanded', 'true');
      // Focus on next frame so the panel is in the DOM and visible
      requestAnimationFrame(() => this.searchInput?.focus());
    }

    closeSearch() {
      if (!this.searchPanel) return;
      this.searchPanel.hidden = true;
      this.searchToggle?.setAttribute('aria-expanded', 'false');
      this.searchToggle?.focus();
    }

    toggleSearch() {
      if (!this.searchPanel) return;
      if (this.searchPanel.hidden) this.openSearch();
      else this.closeSearch();
    }

    handleSearchOutsideClick(event) {
      if (!this.searchPanel || this.searchPanel.hidden) return;
      // Click inside the header (including the panel itself or the toggle) -> ignore
      if (this.contains(event.target)) return;
      this.closeSearch();
    }

    handleCartUpdate(event) {
      const count = event?.cartData?.item_count;
      if (typeof count !== 'number') return;
      const countElement = this.querySelector('[data-wacc-cart-count]');
      if (!countElement) return;
      countElement.textContent = count;
      countElement.toggleAttribute('hidden', count === 0);
    }
  }

  customElements.define('wacc-header', WaccHeader);
}
